import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const CLOSE_DELAY_MS = 200;

/**
 * Drives a hover-triggered popover: opens on mouse enter / keyboard focus and closes after a
 * short delay so the pointer can travel from the trigger into the popover without it snapping
 * shut. Spread `hoverHandlers` on BOTH the trigger and the popover content so either one keeps
 * it open.
 *
 * Uses plain mouse/focus handlers (mirrors `chrome`'s `useHoverFlyout`) rather than react-aria
 * `useHover`: sharing one `useHover` result across the trigger and the popover, combined with a
 * modal `Popover` grabbing focus on open, made the dropdown flicker open/closed. `onFocus`/
 * `onBlur` bubble, so focusing anything inside the popover keeps it open. Pair with a
 * `Popover isNonModal` (see VegaConversationTabs) so opening doesn't move focus or add an
 * underlay. Pass `close` to the popover's `onOpenChange` so Escape / outside clicks dismiss
 * immediately rather than after the delay.
 */
export function useHoverPopover() {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => cancelClose(), [cancelClose]);

  const open = useCallback(() => {
    cancelClose();
    setIsOpen(true);
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS);
  }, [cancelClose]);

  const close = useCallback(() => {
    cancelClose();
    setIsOpen(false);
  }, [cancelClose]);

  const hoverHandlers = useMemo(
    () => ({ onMouseEnter: open, onMouseLeave: scheduleClose, onFocus: open, onBlur: scheduleClose }),
    [open, scheduleClose],
  );

  return { isOpen, open, close, hoverHandlers };
}
